/**
 * SHELF SKINS — cosmetic variants for the greenhouse RACKS (the boards and the posts that carry them),
 * selected by id from the save (`selectedShelfSkin`) and drawn by render/pixi/RackLayer's drawRack().
 *
 * The third member of the house-skin family (sim/houseSkins.ts: shell + floor) and held to the same contract:
 * A SKIN OWNS COLOUR AND MATERIAL, NEVER GEOMETRY. The board heights, the lip line, the overhang, the post
 * pitch and every pot position belong to the layout — pots are placed against the board surface and the
 * sprinklers sit on the top shelf's post intersections, so a skin that moved a board would move the pots off
 * it. A skin recolours the timber and may add one TRIM band under the lip (a metal edge) or NODES (bamboo).
 *
 * "timber" is the CURRENT ART — its numbers are RackLayer's original WOOD / WOOD_HI / WOOD_DK and the
 * 0xe0c090 top-surface tint, moved not redrawn, so an absent or unknown id draws exactly what it always did.
 *
 * THE NEW SKINS ARE DARK. The racks fill a third of the greenhouse; STYLE_GUIDE §2.1 wants the scene's mean
 * value low and the timber's shadow side near-black, so even "Whitewashed Pine" is a warm limed grey, not a
 * white — a white rack would be the brightest thing in the room.
 */

export interface ShelfPalette {
  /** the board's front face — the body colour */
  wood: number;
  /** the lit arris / lit half of each post */
  woodHi: number;
  /** the post body and the grain marks' base */
  woodDk: number;
  /** mixed into `woodHi` for the narrow lit TOP surface of each board (the light lands on the upper face) */
  topTint: number;
  /** the three grain marks on the front face */
  grain: number;
  /** POSTS in a different material from the boards (iron under oak, brass under verdigris) — omitted = the
   *  posts share the board's timber */
  post?: { body: number; hi: number };
  /** a thin metal band along the board's front, just under the lip */
  trim?: number;
  /** bamboo: node rings across the front face instead of grain */
  nodes?: boolean;
}

export interface ShelfSkin {
  id: string;
  name: string;
  desc: string;
  palette: ShelfPalette;
  /** A REWARD skin: locked until earned (or the dev Requirement Override is on). The default is always open. */
  reward?: boolean;
}

export const SHELF_SKINS: ShelfSkin[] = [
  { id: "timber", name: "Greenhouse Timber", desc: "The original varnished timber racks.",
    palette: { wood: 0xa9743f, woodHi: 0xc69056, woodDk: 0x6f4a26, topTint: 0xe0c090, grain: 0x6f4a26 } },
  { id: "whitewash", name: "Whitewashed Pine", desc: "Whitewashed pine with the grain showing through.", reward: true,
    palette: { wood: 0x8f887a, woodHi: 0xb3ab98, woodDk: 0x57524a, topTint: 0xd6cfbc, grain: 0x6a6358 } },
  { id: "walnut", name: "Walnut & Copper", desc: "Dark walnut boards edged in copper.", reward: true,
    palette: { wood: 0x5a3a26, woodHi: 0x7a5236, woodDk: 0x2e1c12, topTint: 0x9a6a44, grain: 0x24160d, trim: 0xb8733a } },
  { id: "pottingOak", name: "Potting-Shed Oak", desc: "Rough oak planks on black iron.", reward: true,
    palette: { wood: 0x7c5a3a, woodHi: 0x9a7450, woodDk: 0x4a331f, topTint: 0xb89a70, grain: 0x3a2818,
      post: { body: 0x2a2e2c, hi: 0x565c58 } } },
  { id: "ironSlate", name: "Black Iron & Slate", desc: "Slate slabs on wrought iron.", reward: true,
    palette: { wood: 0x3f4a48, woodHi: 0x5a6664, woodDk: 0x232a29, topTint: 0x7a8886, grain: 0x2a3230,
      post: { body: 0x1f2322, hi: 0x4a504e } } },
  { id: "verdigris", name: "Verdigris Brass", desc: "Aged brass gone green, with a bright brass edge.", reward: true,
    palette: { wood: 0x4a7263, woodHi: 0x6a9a86, woodDk: 0x2a4239, topTint: 0x8fbba4, grain: 0x2a3d36, trim: 0xc99a38,
      post: { body: 0x7a5a24, hi: 0xc99a38 } } },
  // THE KICKSTARTER BACKER RACK: black nickel boards with a gold trim edge on gold-lit posts. These are the
  // exact values the backer mockup was drawn with (cosmetic-rewards-assets render.mjs "concept-rack-blackNickel",
  // the verdigris rack re-palettted) — backers saw them, so do not warm, darken or age the gold.
  { id: "blackNickel", name: "Black Nickel & Gold", desc: "Gunmetal shelves on gold posts.", reward: true,
    palette: { wood: 0x2b2e33, woodHi: 0x555a62, woodDk: 0x121417, topTint: 0x868c95, grain: 0x1b1d20, trim: 0xd4a53e,
      post: { body: 0x1b1d21, hi: 0xc99a38 } } },
  { id: "bamboo", name: "Bamboo Cane", desc: "Split bamboo tied to cane posts.", reward: true,
    palette: { wood: 0x8a7a48, woodHi: 0xb0a064, woodDk: 0x544826, topTint: 0xd0c088, grain: 0x5a4a22, nodes: true } },
];

export const DEFAULT_SHELF_SKIN = "timber";
const BY_ID = new Map(SHELF_SKINS.map((s) => [s.id, s]));

/** The skin for `id`, falling back to the default for an absent or unknown id. */
export function shelfSkin(id: string | undefined): ShelfSkin {
  return (id && BY_ID.get(id)) || BY_ID.get(DEFAULT_SHELF_SKIN)!;
}
